import { useState, useEffect } from 'react'
import { XMarkIcon, MapPinIcon, CalendarIcon, UserIcon, ExclamationTriangleIcon, ClockIcon } from '@heroicons/react/24/outline'
import { reportsApi, incidentsApi } from '../services/api'
import toast from 'react-hot-toast'

interface IncidentReport {
  id: string
  title: string
  description: string
  reportedAt: string
  location: {
    name: string
    address: string
  }
  severity: {
    label: string
    color: string
  }
  IncidentType: {
    label: string
  }
  User: {
    name: string
    email: string
  }
  organization: {
    name: string
  }
  suspectName?: string
  suspectDescription?: string
  witnesses?: string
  pdIncidentNumber?: string
  pdNameBadge?: string
  pdCalledTime?: string
  pdArrivedTime?: string
  supervisorNotified?: string
  latitude?: number
  longitude?: number
  images?: Array<{
    id: string
    url: string
    filename: string
  }>
  videos?: Array<{
    id: string
    url: string
    filename: string
  }>
  isDraft?: boolean
}

interface HourlyReport {
  id: string
  summary: string
  shiftStart: string
  shiftEnd: string
  activityTags?: string[] | null
  location?: {
    name: string
    address: string
  } | null
  createdBy?: {
    name: string
    email: string
  } | null
  organization?: {
    name: string
    logoUrl?: string
  } | null
  latitude?: number
  longitude?: number
  createdAt: string
  isDraft?: boolean
  clientName?: string
  clientAddress?: string
  officerOnDuty?: string
  officerRelieved?: string | null
  officerWorkingWith?: string | null
  specialAttention?: string | null
  outOfOrderCondition?: string | null
  irregularitiesFound?: Array<{
    item: string
    found: boolean
  }> | null
  hourlyActivityLog?: Array<{ timeRange: string; activity: string }> | string | null
}

interface ReportModalProps {
  isOpen: boolean
  onClose: () => void
  reportId: string | null
  reportType: 'incident' | 'hourly'
}

export default function ReportModal({ isOpen, onClose, reportId, reportType }: ReportModalProps) {
  const [report, setReport] = useState<IncidentReport | HourlyReport | null>(null)
  const [loading, setLoading] = useState(false)
  const [downloadingIncident, setDownloadingIncident] = useState(false)
  const [downloadingHourly, setDownloadingHourly] = useState(false)

  useEffect(() => {
    if (isOpen && reportId) {
      loadReport()
    }
  }, [isOpen, reportId, reportType])

  const loadReport = async () => {
    if (!reportId) return

    setLoading(true)
    try {
      console.log('🔍 Loading report:', { reportId, reportType })
      let response

      if (reportType === 'incident') {
        response = await reportsApi.getIncidentReportById(reportId)
      } else {
        response = await reportsApi.getHourlyReportById(reportId)
      }

      console.log('📄 Report response:', response)
      setReport(response.data)
    } catch (error) {
      console.error('❌ Error loading report:', error)
      toast.error(`Failed to load ${reportType} report`)
    } finally {
      setLoading(false)
    }
  }

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    } catch {
      return 'Invalid date'
    }
  }

  const formatTime = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit'
      })
    } catch {
      return 'Invalid time'
    }
  }

  // Helper function to safely get location name
  const getLocationName = (report: IncidentReport | HourlyReport): string => {
    if (!report) return 'Unknown Location'
    
    if ('location' in report && report.location) {
      if (typeof report.location === 'string') return report.location
      if (typeof report.location === 'object' && report.location.name) return report.location.name
    }
    
    return 'Unknown Location'
  }

  // Helper function to safely get user name
  const getUserName = (report: IncidentReport | HourlyReport): string => {
    if (!report) return 'Unknown User'
    
    if ('User' in report && report.User?.name) return report.User.name
    if ('createdBy' in report && report.createdBy?.name) return report.createdBy.name
    
    return 'Unknown User'
  }

  if (!isOpen) return null

  const isIncidentReport = reportType === 'incident'
  const incidentReport = report as IncidentReport
  const hourlyReport = report as HourlyReport

  console.log('🔍 Report:', report)

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div 
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={onClose}
        />

        {/* Modal panel */}
        <div className="inline-block align-bottom bg-white dark:bg-tech-900 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-tech-700">
            <div className="flex items-center space-x-3">
              <div className={`p-2 rounded-lg ${isIncidentReport ? 'bg-gradient-to-br from-warning to-accent-amber' : 'bg-gradient-to-br from-secondary-500 to-secondary-600'}`}>
                {isIncidentReport ? (
                  <ExclamationTriangleIcon className="h-6 w-6 text-white" />
                ) : (
                  <ClockIcon className="h-6 w-6 text-white" />
                )}
              </div>
              <div>
                <h3 className="text-heading-lg text-gray-900 dark:text-tech-50 font-display">
                  {isIncidentReport ? 'Incident Report' : 'Hourly Report'}
                </h3>
                <p className="text-body-sm text-gray-600 dark:text-tech-400">
                  View {isIncidentReport ? 'incident' : 'hourly'} details
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-tech-300 transition-colors"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 max-h-96 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="flex flex-col items-center space-y-4">
                  <div className="relative">
                    <div className="w-8 h-8 border-2 border-gray-300 dark:border-tech-600 rounded-full animate-spin border-t-primary-500"></div>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-tech-400">Loading {isIncidentReport ? 'incident' : 'hourly'} report...</p>
                </div>
              </div>
            ) : report ? (
              <div className="space-y-6">
                {isIncidentReport ? (
                  // Incident Report Content
                  <>
                    {/* Title */}
                    <div>
                      <h4 className="text-heading-lg text-gray-900 dark:text-tech-50 font-display mb-4">
                        {incidentReport.title}
                      </h4>
                    </div>

                    {/* Description */}
                    <div>
                      <h5 className="text-heading-sm text-gray-900 dark:text-tech-50 font-display mb-2">
                        Description
                      </h5>
                      <p className="text-body text-gray-700 dark:text-tech-300 whitespace-pre-wrap">
                        {incidentReport.description}
                      </p>
                    </div>

                    {/* Basic Information */}
                    <div className="space-y-4">
                      <h5 className="text-heading-sm text-gray-900 dark:text-tech-50 font-display">
                        Incident Details
                      </h5>

                      {/* Metadata */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="flex items-center space-x-2">
                          <MapPinIcon className="h-4 w-4 text-gray-500 dark:text-tech-400" />
                          <span className="text-body-sm text-gray-600 dark:text-tech-400">
                            {getLocationName(incidentReport)}
                          </span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <CalendarIcon className="h-4 w-4 text-gray-500 dark:text-tech-400" />
                          <span className="text-body-sm text-gray-600 dark:text-tech-400">
                            {formatDate(incidentReport.reportedAt)}
                          </span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <UserIcon className="h-4 w-4 text-gray-500 dark:text-tech-400" />
                          <span className="text-body-sm text-gray-600 dark:text-tech-400">
                            {getUserName(incidentReport)}
                          </span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <ExclamationTriangleIcon className="h-4 w-4 text-gray-500 dark:text-tech-400" />
                          <span className="text-body-sm text-gray-600 dark:text-tech-400">
                            {incidentReport.IncidentType?.label || 'Unknown Type'}
                          </span>
                        </div>
                      </div>

                      {/* Severity */}
                      {incidentReport.severity && (
                        <div className="flex items-center space-x-2">
                          <span className="text-body-sm font-medium text-gray-700 dark:text-tech-300">
                            Severity:
                          </span>
                          <span 
                            className="px-2 py-1 rounded-full text-xs font-medium"
                            style={{ 
                              backgroundColor: `${incidentReport.severity.color}20`,
                              color: incidentReport.severity.color,
                              border: `1px solid ${incidentReport.severity.color}40`
                            }}
                          >
                            {incidentReport.severity.label}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Additional Details */}
                    {(incidentReport.suspectName || incidentReport.suspectDescription || incidentReport.witnesses) && (
                      <div className="space-y-3">
                        <h5 className="text-heading-sm text-gray-900 dark:text-tech-50 font-display">
                          Additional Details
                        </h5>
                        <div className="space-y-2">
                          {incidentReport.suspectName && (
                            <div>
                              <span className="text-body-sm font-medium text-gray-700 dark:text-tech-300">
                                Suspect Name:
                              </span>
                              <span className="text-body-sm text-gray-600 dark:text-tech-400 ml-2">
                                {incidentReport.suspectName}
                              </span>
                            </div>
                          )}
                          {incidentReport.suspectDescription && (
                            <div>
                              <span className="text-body-sm font-medium text-gray-700 dark:text-tech-300">
                                Suspect Description:
                              </span>
                              <span className="text-body-sm text-gray-600 dark:text-tech-400 ml-2">
                                {incidentReport.suspectDescription}
                              </span>
                            </div>
                          )}
                          {incidentReport.witnesses && (
                            <div>
                              <span className="text-body-sm font-medium text-gray-700 dark:text-tech-300">
                                Witnesses:
                              </span>
                              <span className="text-body-sm text-gray-600 dark:text-tech-400 ml-2">
                                {incidentReport.witnesses}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Police Department Information */}
                    {(incidentReport.pdIncidentNumber || incidentReport.pdNameBadge || incidentReport.pdCalledTime || incidentReport.pdArrivedTime) && (
                      <div className="space-y-3">
                        <h5 className="text-heading-sm text-gray-900 dark:text-tech-50 font-display">
                          Police Department Information
                        </h5>
                        <div className="space-y-2">
                          {incidentReport.pdIncidentNumber && (
                            <div>
                              <span className="text-body-sm font-medium text-gray-700 dark:text-tech-300">
                                Incident Number:
                              </span>
                              <span className="text-body-sm text-gray-600 dark:text-tech-400 ml-2">
                                {incidentReport.pdIncidentNumber}
                              </span>
                            </div>
                          )}
                          {incidentReport.pdNameBadge && (
                            <div>
                              <span className="text-body-sm font-medium text-gray-700 dark:text-tech-300">
                                Officer Name/Badge:
                              </span>
                              <span className="text-body-sm text-gray-600 dark:text-tech-400 ml-2">
                                {incidentReport.pdNameBadge}
                              </span>
                            </div>
                          )}
                          {incidentReport.pdCalledTime && (
                            <div>
                              <span className="text-body-sm font-medium text-gray-700 dark:text-tech-300">
                                PD Called:
                              </span>
                              <span className="text-body-sm text-gray-600 dark:text-tech-400 ml-2">
                                {formatTime(incidentReport.pdCalledTime)}
                              </span>
                            </div>
                          )}
                          {incidentReport.pdArrivedTime && (
                            <div>
                              <span className="text-body-sm font-medium text-gray-700 dark:text-tech-300">
                                PD Arrived:
                              </span>
                              <span className="text-body-sm text-gray-600 dark:text-tech-400 ml-2">
                                {formatTime(incidentReport.pdArrivedTime)}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Supervisor Notification */}
                    {incidentReport.supervisorNotified && (
                      <div className="space-y-3">
                        <h5 className="text-heading-sm text-gray-900 dark:text-tech-50 font-display">
                          Supervisor Notification
                        </h5>
                        <p className="text-body-sm text-gray-600 dark:text-tech-400">
                          {incidentReport.supervisorNotified}
                        </p>
                      </div>
                    )}

                    {/* Media Files */}
                    {((incidentReport.images && incidentReport.images.length > 0) || (incidentReport.videos && incidentReport.videos.length > 0)) && (
                      <div className="space-y-3">
                        <h5 className="text-heading-sm text-gray-900 dark:text-tech-50 font-display">
                          Media Files
                        </h5>
                        
                        {incidentReport.images && incidentReport.images.length > 0 && (
                          <div>
                            <span className="text-body-sm font-medium text-gray-700 dark:text-tech-300 mb-2 block">
                              Images ({incidentReport.images.length}):
                            </span>
                            <div className="grid grid-cols-2 gap-3">
                              {incidentReport.images.map((image, index) => (
                                <div key={image.id} className="relative group">
                                  <img
                                    src={(image as any).url || (image as any).fileUrl}
                                    alt={`Image ${index + 1}`}
                                    className="w-full h-32 object-cover rounded-lg border border-gray-200 dark:border-tech-600 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                                    onClick={() => window.open((image as any).url || (image as any).fileUrl, '_blank')}
                                    onLoad={() => console.log('🔍 App Modal - Image loaded successfully:', (image as any).url || (image as any).fileUrl)}
                                    onError={(e) => {
                                      const target = e.target as HTMLImageElement;
                                      target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMjAwIiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik02MCA4MEgxNDBWMTIwSDYwVjgwWiIgZmlsbD0iIzlDQTNBRiIvPgo8cGF0aCBkPSJNNjAgNjBIMTQwVjgwSDYwVjYwWiIgZmlsbD0iIzlDQTNBRiIvPgo8L3N2Zz4K';
                                      target.className = 'w-full h-32 object-cover rounded-lg border border-gray-200 dark:border-tech-600 shadow-sm hover:shadow-md transition-shadow cursor-pointer bg-gray-100 dark:bg-tech-800';
                                    }}
                                  />
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {incidentReport.videos && incidentReport.videos.length > 0 && (
                          <div>
                            <span className="text-body-sm font-medium text-gray-700 dark:text-tech-300 mb-2 block">
                              Videos ({incidentReport.videos.length}):
                            </span>
                            <div className="grid grid-cols-1 gap-3">
                              {incidentReport.videos.map((video, index) => (
                                <div key={video.id} className="relative group">
                                  <video
                                    src={(video as any).url || (video as any).fileUrl}
                                    className="w-full h-40 object-cover rounded-lg border border-gray-200 dark:border-tech-600 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                                    controls
                                    preload="metadata"
                                    onError={(e) => {
                                      const target = e.target as HTMLVideoElement;
                                      target.style.display = 'none';
                                      const parent = target.parentElement;
                                      if (parent) {
                                        parent.innerHTML = `
                                          <div class="w-full h-40 bg-gray-100 dark:bg-tech-800 rounded-lg border border-gray-200 dark:border-tech-600 flex items-center justify-center">
                                            <div class="text-center">
                                              <svg class="w-8 h-8 text-gray-400 dark:text-tech-500 mx-auto mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14.828 14.828a4 4 0 01-5.656 0M9 10h1m4 0h1m-6 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                                              </svg>
                                              <p class="text-xs text-gray-500 dark:text-tech-400">Video not available</p>
                                            </div>
                                          </div>
                                        `;
                                      }
                                    }}
                                  />
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </>
                ) : (
                  // Hourly Report Content
                  <>
                    {/* Summary */}
                    <div>
                      <h4 className="text-heading-lg text-gray-900 dark:text-tech-50 font-display mb-4">
                        Hourly Report Summary
                      </h4>
                      <p className="text-body text-gray-700 dark:text-tech-300 whitespace-pre-wrap">
                        {hourlyReport.summary}
                      </p>
                    </div>

                    {/* Basic Information */}
                    <div className="space-y-4">
                      <h5 className="text-heading-sm text-gray-900 dark:text-tech-50 font-display">
                        Shift Details
                      </h5>

                      {/* Metadata */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="flex items-center space-x-2">
                          <MapPinIcon className="h-4 w-4 text-gray-500 dark:text-tech-400" />
                          <span className="text-body-sm text-gray-600 dark:text-tech-400">
                            {getLocationName(hourlyReport)}
                          </span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <UserIcon className="h-4 w-4 text-gray-500 dark:text-tech-400" />
                          <span className="text-body-sm text-gray-600 dark:text-tech-400">
                            {getUserName(hourlyReport)}
                          </span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <ClockIcon className="h-4 w-4 text-gray-500 dark:text-tech-400" />
                          <span className="text-body-sm text-gray-600 dark:text-tech-400">
                            Shift Start: {formatTime(hourlyReport.shiftStart)}
                          </span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <ClockIcon className="h-4 w-4 text-gray-500 dark:text-tech-400" />
                          <span className="text-body-sm text-gray-600 dark:text-tech-400">
                            Shift End: {formatTime(hourlyReport.shiftEnd)}
                          </span>
                        </div>
                      </div>

                      {/* Activity Tags */}
                      {hourlyReport.activityTags && hourlyReport.activityTags.length > 0 && (
                        <div className="space-y-2">
                          <span className="text-body-sm font-medium text-gray-700 dark:text-tech-300">
                            Activity Tags:
                          </span>
                          <div className="flex flex-wrap gap-2">
                            {hourlyReport.activityTags.map((tag, index) => (
                              <span
                                key={index}
                                className="px-2 py-1 bg-secondary-100 dark:bg-secondary-900/30 text-secondary-700 dark:text-secondary-300 rounded-full text-xs font-medium"
                              >
                                {tag}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Created Date */}
                      <div className="flex items-center space-x-2">
                        <CalendarIcon className="h-4 w-4 text-gray-500 dark:text-tech-400" />
                        <span className="text-body-sm text-gray-600 dark:text-tech-400">
                          Created: {formatDate(hourlyReport.createdAt)}
                        </span>
                      </div>
                    </div>

                    {/* Client Information */}
                    {(hourlyReport.clientName || hourlyReport.clientAddress) && (
                      <div className="space-y-3">
                        <h5 className="text-heading-sm text-gray-900 dark:text-tech-50 font-display">
                          Client Information
                        </h5>
                        <div className="space-y-2">
                          {hourlyReport.clientName && (
                            <div>
                              <span className="text-body-sm font-medium text-gray-700 dark:text-tech-300">
                                Client Name:
                              </span>
                              <span className="text-body-sm text-gray-600 dark:text-tech-400 ml-2">
                                {hourlyReport.clientName}
                              </span>
                            </div>
                          )}
                          {hourlyReport.clientAddress && (
                            <div>
                              <span className="text-body-sm font-medium text-gray-700 dark:text-tech-300">
                                Client Address:
                              </span>
                              <span className="text-body-sm text-gray-600 dark:text-tech-400 ml-2">
                                {hourlyReport.clientAddress}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Officer Information */}
                    {(hourlyReport.officerOnDuty || hourlyReport.officerRelieved || hourlyReport.officerWorkingWith) && (
                      <div className="space-y-3">
                        <h5 className="text-heading-sm text-gray-900 dark:text-tech-50 font-display">
                          Officer Information
                        </h5>
                        <div className="space-y-2">
                          {hourlyReport.officerOnDuty && (
                            <div>
                              <span className="text-body-sm font-medium text-gray-700 dark:text-tech-300">
                                Officer on Duty:
                              </span>
                              <span className="text-body-sm text-gray-600 dark:text-tech-400 ml-2">
                                {hourlyReport.officerOnDuty}
                              </span>
                            </div>
                          )}
                          {hourlyReport.officerRelieved && (
                            <div>
                              <span className="text-body-sm font-medium text-gray-700 dark:text-tech-300">
                                Officer Relieved:
                              </span>
                              <span className="text-body-sm text-gray-600 dark:text-tech-400 ml-2">
                                {hourlyReport.officerRelieved}
                              </span>
                            </div>
                          )}
                          {hourlyReport.officerWorkingWith && (
                            <div>
                              <span className="text-body-sm font-medium text-gray-700 dark:text-tech-300">
                                Working With:
                              </span>
                              <span className="text-body-sm text-gray-600 dark:text-tech-400 ml-2">
                                {hourlyReport.officerWorkingWith}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Special Attention & Out of Order */}
                    {(hourlyReport.specialAttention || hourlyReport.outOfOrderCondition) && (
                      <div className="space-y-3">
                        <h5 className="text-heading-sm text-gray-900 dark:text-tech-50 font-display">
                          Special Conditions
                        </h5>
                        <div className="space-y-2">
                          {hourlyReport.specialAttention && hourlyReport.specialAttention !== 'null' && (
                            <div>
                              <span className="text-body-sm font-medium text-gray-700 dark:text-tech-300">
                                Special Attention:
                              </span>
                              <span className="text-body-sm text-gray-600 dark:text-tech-400 ml-2">
                                {hourlyReport.specialAttention}
                              </span>
                            </div>
                          )}
                          {hourlyReport.outOfOrderCondition && hourlyReport.outOfOrderCondition !== 'null' && (
                            <div>
                              <span className="text-body-sm font-medium text-gray-700 dark:text-tech-300">
                                Out of Order Condition:
                              </span>
                              <span className="text-body-sm text-gray-600 dark:text-tech-400 ml-2">
                                {hourlyReport.outOfOrderCondition}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Irregularities Found */}
                    {hourlyReport.irregularitiesFound && hourlyReport.irregularitiesFound.length > 0 && (
                      <div className="space-y-3">
                        <h5 className="text-heading-sm text-gray-900 dark:text-tech-50 font-display">
                          Irregularities Check
                        </h5>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {hourlyReport.irregularitiesFound.map((irregularity, index) => (
                            <div key={index} className="flex items-center space-x-2">
                              <span className={`w-3 h-3 rounded-full ${irregularity.found ? 'bg-red-500' : 'bg-green-500'}`}></span>
                              <span className="text-body-sm text-gray-600 dark:text-tech-400">
                                {irregularity.item}: {irregularity.found ? 'Found' : 'Clear'}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Hourly Activity Log */}
                    {hourlyReport.hourlyActivityLog && (
                      <div className="space-y-3">
                        <h5 className="text-heading-sm text-gray-900 dark:text-tech-50 font-display">
                          Hourly Activity Log
                        </h5>
                        {Array.isArray(hourlyReport.hourlyActivityLog) ? (
                          <div className="space-y-3">
                            {hourlyReport.hourlyActivityLog.map((entry, index) => (
                              <div key={index} className="bg-gray-50 dark:bg-tech-800/30 p-3 rounded-lg">
                                <div className="flex items-center justify-between mb-1">
                                  <span className="text-xs font-medium text-gray-500 dark:text-tech-400">
                                    Entry #{index + 1}
                                  </span>
                                  <span className="text-xs text-gray-600 dark:text-tech-400 font-mono">
                                    {entry.timeRange}
                                  </span>
                                </div>
                                <p className="text-body-sm text-gray-900 dark:text-tech-100">
                                  {entry.activity}
                                </p>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-body-sm text-gray-600 dark:text-tech-400 whitespace-pre-wrap">
                            {hourlyReport.hourlyActivityLog}
                          </p>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            ) : (
              <div className="text-center py-12">
                {isIncidentReport ? (
                  <ExclamationTriangleIcon className="h-12 w-12 text-gray-400 dark:text-tech-500 mx-auto mb-4" />
                ) : (
                  <ClockIcon className="h-12 w-12 text-gray-400 dark:text-tech-500 mx-auto mb-4" />
                )}
                <h3 className="text-heading-lg text-gray-900 dark:text-tech-50 font-display mb-2">
                  Report Not Found
                </h3>
                <p className="text-body text-gray-600 dark:text-tech-400">
                  The {isIncidentReport ? 'incident' : 'hourly'} report could not be loaded.
                </p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex justify-between items-center p-6 border-t border-gray-200 dark:border-tech-700">
            {report && !report.isDraft && isIncidentReport && (
              <button
                onClick={async () => {
                  if (incidentReport?.id) {
                    try {
                      setDownloadingIncident(true)
                      
                      // Download PDF using the API
                      const blob = await incidentsApi.downloadIncidentPdf(incidentReport.id)
                      
                      // Create download link
                      const url = window.URL.createObjectURL(blob)
                      const link = document.createElement('a')
                      link.href = url
                      link.download = `incident-report-${incidentReport.id}.pdf`
                      document.body.appendChild(link)
                      link.click()
                      document.body.removeChild(link)
                      window.URL.revokeObjectURL(url)
                      
                      toast.success('Incident report downloaded successfully')
                    } catch (error) {
                      console.error('Error downloading PDF:', error)
                      toast.error('Failed to download PDF')
                    } finally {
                      setDownloadingIncident(false)
                    }
                  }
                }}
                disabled={downloadingIncident}
                className="flex items-center space-x-2 px-4 py-2 text-body-sm font-medium text-blue-600 dark:text-blue-400 bg-blue-100 dark:bg-blue-900/30 rounded-lg hover:bg-blue-200 dark:hover:bg-blue-900/50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <span>{downloadingIncident ? 'Downloading...' : 'Download PDF'}</span>
              </button>
            )}
            {report && !report.isDraft && !isIncidentReport && (
              <button
                onClick={async () => {
                  if (hourlyReport?.id) {
                    try {
                      setDownloadingHourly(true)
                      
                      // Download PDF using the API
                      const blob = await reportsApi.downloadHourlyReportPdf(hourlyReport.id)
                      
                      // Create download link
                      const url = window.URL.createObjectURL(blob)
                      const link = document.createElement('a')
                      link.href = url
                      link.download = `hourly-report-${hourlyReport.id}.pdf`
                      document.body.appendChild(link)
                      link.click()
                      document.body.removeChild(link)
                      window.URL.revokeObjectURL(url)
                      
                      toast.success('Hourly report downloaded successfully')
                    } catch (error) {
                      console.error('Error downloading PDF:', error)
                      toast.error('Failed to download PDF')
                    } finally {
                      setDownloadingHourly(false)
                    }
                  }
                }}
                disabled={downloadingHourly}
                className="flex items-center space-x-2 px-4 py-2 text-body-sm font-medium text-blue-600 dark:text-blue-400 bg-blue-100 dark:bg-blue-900/30 rounded-lg hover:bg-blue-200 dark:hover:bg-blue-900/50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <span>{downloadingHourly ? 'Downloading...' : 'Download PDF'}</span>
              </button>
            )}
            <button
              onClick={onClose}
              className="px-4 py-2 text-body-sm font-medium text-gray-700 dark:text-tech-300 bg-gray-100 dark:bg-tech-800 rounded-lg hover:bg-gray-200 dark:hover:bg-tech-700 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  )
} 